import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

// One import is the whole design system: tokens are CSS custom properties, and every
// component below reads them (architecture §3.1).
import "@don/design-system/styles.css";

import { App } from "./App.js";
import { API_BASE } from "./backend/adapter.js";
import { AdminCallbackApp } from "./connect/AdminCallback.js";
import { ConnectApp } from "./connect/ConnectApp.js";
import { connectPort, takeToken, timedOut } from "./connect/session.js";
import { ErrorBoundary } from "./reporting/ErrorBoundary.js";
import { appVersionOf, createReporter, postWith, reportableAt } from "./reporting/reporter.js";
import { parseRoute } from "./routing/route.js";

/**
 * The Console's version, injected by Vite from `package.json` (`vite.config.ts`). A build or
 * harness without the constant reports no `app_version`, which the server logs `unknown`.
 */
declare const __CONSOLE_VERSION__: string | undefined;

const root = document.getElementById("root");
if (!root) {
  throw new Error("the Console needs a #root element to mount into");
}

// The error reporter (issue 243), installed before the first render so a crash while mounting
// is reported too. Never on the signed-out sign-in page (PRD "Console reporter").
const reporter = createReporter({
  post: postWith(fetch, API_BASE),
  appVersion: appVersionOf(typeof __CONSOLE_VERSION__ === "string" ? __CONSOLE_VERSION__ : undefined),
  now: () => Date.now(),
  reportable: () => reportableAt(window.location.pathname),
});
reporter.install(window);

// The employee's Connections page (issue 392, design-spec §8 W1) is its own app, outside the
// owner shell: no owner read, no 401 redirect to `/sign-in`, and the reporter stays silent there
// (`reportableAt`). The Connect link's token is taken off the URL, and the fragment stripped,
// before anything renders or is sent (§5.5 D3). The api's `#timed-out` marker is read first.
// The admin callback page (issue 466, Outlook design-spec W5) is outside the shell too, and reads
// nothing but its own query: the IT admin holds no Don session of any kind.
const kind = parseRoute(window.location.pathname).kind;
const connect = kind === "connect";
const arrivedTimedOut = connect && timedOut(window.location);
const page =
  kind === "admin-callback" ? (
    <AdminCallbackApp search={window.location.search} />
  ) : connect ? (
    <ConnectApp port={connectPort(fetch, API_BASE)} timedOut={arrivedTimedOut} token={takeToken(window.location, window.history)} />
  ) : (
    <App onApiFailure={reporter.report} />
  );

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary reporter={reporter}>{page}</ErrorBoundary>
  </StrictMode>,
);
